import { Stack, Typography, Divider, Skeleton } from "@mui/material";
import { useSelector } from "react-redux";
import { useOpportunityCardsQuery } from "src/context/api/non-branded/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { RootState } from "src/context/store";
import { fDate } from "src/utils/format-time";
import { fNumber } from "src/utils/format-number";
import { OpportunityCard } from "src/context/api/non-branded/models";

interface OpportunityColumnProps {
    title: string;
    color: string;
    data: OpportunityCard | undefined;
    isLoading: boolean;
}

const OpportunityColumn = ({ title, color, data, isLoading }: OpportunityColumnProps) => {
    const searchTerms = data?.search_terms || [];
    const count = data?.count || 0;

    return (
        <Stack sx={{ backgroundColor: "background.paper" }} borderRadius={2} border={1} borderColor="divider" spacing={2} flex={1}>
            <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" m={0} p={0} borderBottom={1} borderColor="divider">
                    <Stack width="33%">
                        <Typography
                            px={2}
                            py={1}
                            variant="subtitle1"
                            sx={{
                                color: color,
                                fontWeight: 600,
                            }}
                        >
                            {title}
                        </Typography>
                        <Divider  sx={{ borderColor: color, borderWidth: 1 }} />
                    </Stack>
                    <Typography
                        variant="subtitle1"
                        px={2}
                        sx={{
                            color: color,
                            fontWeight: 600,
                        }}
                    >
                        {isLoading ? <Skeleton width={20} /> : count}
                    </Typography>
                </Stack>
            </Stack>
            <Stack spacing={1.5}>
                {isLoading ? (
                    <>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                                <Skeleton width="60%" height={20} />
                                <Skeleton width={60} height={20} />
                            </Stack>
                        ))}
                    </>
                ) : (
                    searchTerms.map((term, index) => (
                        <>
                            <Stack
                                key={index}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                px={2}
                                py={0}
                                pb={index === searchTerms.length - 1 ? 1.5 : 0}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "text.primary",
                                    }}
                                >
                                    {term.search_term}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "text.secondary",
                                    }}
                                >
                                    {fNumber(term.total_clicks)}
                                </Typography>
                            </Stack>
                            {index !== searchTerms.length - 1 && (
                                <Divider sx={{ borderColor: "divider", borderWidth: 1, borderStyle: "dashed" }} />
                            )}
                        </>
                    ))
                )}
            </Stack>
        </Stack>
    );
};

export default function TopOpportunities() {
    const workspace = useCurrentWorkspace();
    const filterStore = useSelector((state: RootState) => state.filters);

    const { data: opportunityCards, isLoading } = useOpportunityCardsQuery(
        {
            workspace_id: workspace?.id as number,
            start_date: fDate(filterStore.start, "yyyy-MM-dd"),
            end_date: fDate(filterStore.end, "yyyy-MM-dd"),
            devices: filterStore.devices,
            countries: filterStore.countries,
            platforms: filterStore.platforms,
        },
        { skip: !workspace?.id }
    );

    const overlapData = opportunityCards?.data.find((card) => card.opportunity === "overlap");
    const seoBoostData = opportunityCards?.data.find((card) => card.opportunity === "seo_boost");
    const seaBoostData = opportunityCards?.data.find((card) => card.opportunity === "sea_boost");

    return (
        <Stack spacing={2} px={0} mx={0} width="100%">
            <Stack direction="row" px={2} py={1} pb={2} borderBottom={1} borderColor="divider" justifyContent="space-between" alignItems="center">
                <Typography pt={1.5} variant="h6" sx={{ color: "text.primary" }}>
                    Top opportunities
                </Typography>
                <Typography pt={1.5} variant="body2" sx={{ color: "text.secondary" }}>
                    Sorted by clicks
                </Typography>
            </Stack>
            <Stack px={2} py={1} pb={2} direction="row" spacing={3}>
                <OpportunityColumn
                    title="Overlap"
                    color="#20D873"
                    data={overlapData}
                    isLoading={isLoading}
                />
                <OpportunityColumn
                    title="SEO boost"
                    color="#DBC272"
                    data={seoBoostData}
                    isLoading={isLoading}
                />
                <OpportunityColumn
                    title="SEA boost"
                    color="#CEA8F0"
                    data={seaBoostData}
                    isLoading={isLoading}
                />
            </Stack>
        </Stack>
    );
}
